import React, { useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import PropTypes from 'prop-types'

import chips from '../../../common/ui/chips'

import { CustomerThreadMessage } from './CustomerThreadMessage'
import { SupportThreadMessage } from './SupportThreadMessage'

export function TicketDrawer({
  toggleDrawer,
  isDrawerOpen,
  ticketDetails,
  tickets,
  replyMessage,
  setReplyMessage,
  handleUpdateTicket,
  setTicketClosed,
  role
}) {
  return (
    <Drawer
      anchor='right'
      open={isDrawerOpen.right}
      onClose={toggleDrawer('right', false)}
    >
      <ViewTicket
        toggleDrawer={toggleDrawer}
        ticketDetails={ticketDetails}
        tickets={tickets}
        replyMessage={replyMessage}
        setReplyMessage={setReplyMessage}
        handleUpdateTicket={handleUpdateTicket}
        setTicketClosed={setTicketClosed}
        role={role}
      />
    </Drawer>
  )
}

TicketDrawer.propTypes = {
  toggleDrawer: PropTypes.func,
  isDrawerOpen: PropTypes.object,
  ticketDetails: PropTypes.object,
  tickets: PropTypes.array,
  replyMessage: PropTypes.string,
  setReplyMessage: PropTypes.func,
  handleUpdateTicket: PropTypes.func,
  setTicketClosed: PropTypes.func,
  role: PropTypes.string
}

export function ViewTicket({
  toggleDrawer,
  ticketDetails,
  tickets,
  replyMessage,
  setReplyMessage,
  handleUpdateTicket,
  role
}) {
  const messagesEndRef = useRef(null)
  const scrollToBottom = () => {
    messagesEndRef.current.scrollIntoView({
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom()
    }, 300)
  }, [tickets])

  return (
    <Box
      sx={{
        p: '0 30px',
        marginRight: '20px',
        width: '40vw'
      }}
      role='presentation'
    >
      <Box width='100%'>
        <IconButton
          aria-label='close'
          onClick={toggleDrawer('right', false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (_theme) => _theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ padding: `5px 14px`, width: '100%' }}>
        <Typography
          sx={{
            fontColor: 'blue'
          }}
          variant='h5'
        >
          #{ticketDetails.ticketNo}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='subtitle1'>
            Category: {ticketDetails.subject}
          </Typography>
          {chips.support[ticketDetails.status.toUpperCase()]}
        </Box>
        <Divider sx={{ width: '100%', m: 0 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='subtitle1'>
            <b>Email:</b> {ticketDetails.userEmail}
          </Typography>
          <Typography variant='subtitle1'>
            <b>Organization: </b>
            {ticketDetails.orgName}
          </Typography>
        </Box>
        <Divider sx={{ width: '100%', m: 0 }} />
        <Box sx={{ maxHeight: '50vh', overflow: 'scroll' }}>
          <CustomerThreadMessage
            message={ticketDetails.message}
            author={ticketDetails.userName}
            sentTime={ticketDetails.createdAt}
            subHead='Initial Report'
          />
          {ticketDetails.replies &&
            ticketDetails.replies.map((reply) =>
              reply.authorId === ticketDetails.userId ? (
                <CustomerThreadMessage
                  key={reply}
                  message={reply.message}
                  author={reply.author}
                  sentTime={reply.repliedAt}
                />
              ) : (
                <SupportThreadMessage
                  key={reply}
                  message={reply.message}
                  author={reply.author}
                  sentTime={reply.repliedAt}
                />
              )
            )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          padding: '0px 24px'
        }}
      >
        <Box
          sx={{
            width: '100%',
            border: 'none',
            outline: 'none',
            marginBottom: '40px',
            marginTop: '10px'
          }}
        >
          <TextField
            inputProps={{ 'data-cy': 'replyBox' }}
            fullWidth
            multiline
            rows={4}
            label='Reply'
            disabled={ticketDetails.status === 'Resolved'}
            placeholder='Your reply here...'
            required
            onChange={(e) => {
              setReplyMessage(e.target.value)
            }}
            value={replyMessage}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'right',
          width: '100%',
          padding: '5px 24px'
        }}
      >
        {/* awaiting design for this button */}
        {['opsowner', 'ops'].includes(role) ? (
          <Button
            size='small'
            disabled={ticketDetails.status === 'Resolved'}
            variant='filled'
            onClick={() => {
              handleUpdateTicket('resolve')
            }}
            data-cy='resolveBtn'
          >
            Mark Resolved
          </Button>
        ) : null}
        <Button
          size='small'
          variant='secondary'
          onClick={toggleDrawer('right', false)}
          data-cy='replyBackBtn'
        >
          Back
        </Button>
        <Button
          size='small'
          variant='filled'
          disabled={ticketDetails.status === 'Resolved'}
          onClick={() => {
            handleUpdateTicket('reply')
          }}
          data-cy='replySendBtn'
        >
          Send
        </Button>
      </Box>
    </Box>
  )
}

ViewTicket.propTypes = {
  toggleDrawer: PropTypes.func,
  ticketDetails: PropTypes.object,
  tickets: PropTypes.array,
  replyMessage: PropTypes.string,
  setReplyMessage: PropTypes.func,
  handleUpdateTicket: PropTypes.func,
  role: PropTypes.string
}
